import { APICONFIG } from '@config/api.config';
import { APIService } from '@services/api/common-services';
import { useQuery } from '@tanstack/react-query';
import { IQueryResponse } from '@services/api';
import { useEffect, useMemo } from 'react';
import { useRbdmMeetCompareStore } from '@store/rbdmMeetCompareStore';
import { useGetRxDb } from '@hooks/getRxdbData';
import { ProfileData } from '../profile';

export interface IRbdmMeetCompare {
	'Completed Meeting': number;
	Scheduled_Meeting: number;
	mat_today_meeting: number;
	conducted_meeting: number;
}

export const useRbdmMeetCompare = (
	enabled: boolean = true,
	mdicode?: string
): IQueryResponse<IRbdmMeetCompare | null> => {
	const { setMeetCompareData, setLoading, setError } = useRbdmMeetCompareStore();
	const { result: profileData } = useGetRxDb<ProfileData>('profile');

	const profile = profileData?.[0];
	const isRBDM = profile?.usertype_c === 'RBDM';
	const isProfileReady = profileData !== undefined && profile !== undefined;

	// This is the ONLY thing that allows the query to run
	const shouldEnableQuery = enabled && isProfileReady && isRBDM;

	// Prevent query from even being registered if not allowed
	const queryKey = useMemo(() => {
		if (!shouldEnableQuery) return null; // Important: null key = no query
		return ['rbdmMeetCompare', mdicode];
	}, [shouldEnableQuery, mdicode]);

	const query = useQuery({
		queryKey: queryKey ?? ['disabled-rbdm-query'], // dummy key when blocked
		queryFn: async (): Promise<IRbdmMeetCompare> => {
			// Final safety gate - this will throw if somehow called
			if (!isRBDM) {
				throw new Error('Unauthorized: RBDM only');
			}
			const params = mdicode?.trim() ? { cmdi_code: mdicode.trim() } : undefined;
			return APIService.getData<IRbdmMeetCompare>(APICONFIG.RBDM_MEET_COMPARE, params);
		},
		enabled: !!queryKey && shouldEnableQuery, // Double protection
		refetchOnWindowFocus: false,
		retry: false,
		staleTime: 5 * 60 * 1000,
		gcTime: 10 * 60 * 1000,
		placeholderData: undefined,
	});

	const { data, error, isLoading, isFetching, refetch } = query;

	// Sync store
	useEffect(() => {
		if (!isProfileReady) {
			setLoading(true);
			setError(null);
			return;
		}

		if (!isRBDM) {
			setError(new Error('Access restricted to RBDM users'));
			setLoading(false);
			return;
		}

		if (data) {
			setMeetCompareData({
				'Completed Meeting': data['Completed Meeting'] ?? 0,
				Scheduled_Meeting: data.Scheduled_Meeting ?? 0,
				mat_today_meeting: data.mat_today_meeting ?? 0,
				conducted_meeting: data.conducted_meeting ?? 0,
			});
		}

		setError(error ?? null);
		setLoading(isLoading || isFetching);
	}, [data, error, isLoading, isFetching, isProfileReady, isRBDM, setMeetCompareData, setError, setLoading]);

	// Final return - safe for all cases
	if (!isProfileReady) {
		return {
			data: null,
			error: null,
			isLoading: true,
			isPending: true,
			isFetching: false,
		};
	}

	if (!isRBDM) {
		return {
			data: null,
			error: new Error('RBDM access only'),
			isLoading: false,
			isPending: false,
			isFetching: false,
		};
	}

	return {
		data: data ?? null,
		error,
		isLoading,
		isPending: query.isPending,
		isFetching,
		refetch,
	};
};
